import { useGetOverviews } from 'api/hooks/useGetOverviews';
import { SummaryCard } from 'features/Layout/PageActions';
import React from 'react';
import { Card, Badge, Button, Icon, Loader } from 'ui';
import { motion } from 'framer-motion';
import { useStackAnimation } from 'hooks/useStackAnimation';
import { ActionTitle } from 'app/features/Layout/PageActions';
import { FormatCurrency } from 'utils/features/formatCurrency';

export const Dashboard = () => {
	const { data, isLoading } = useGetOverviews();
	const { budget, invoices, proposals, items, retainerFee } = data?.data || {};

	const openProposals =
		proposals?.filter((proposal) => proposal.status === 'opened') || [];
	const openProposalsTotal = openProposals.reduce(
		(sum, proposal) => sum + proposal.total,
		0,
	);

	const openInvoices =
		invoices?.filter(
			(invoice) => invoice.status === 'opened' || invoice.status === 'pending',
		) || [];
	const openInvoicesTotal = openInvoices.reduce(
		(sum, invoice) => sum + invoice.total,
		0,
	);

	const itemsToReview =
		items?.filter((item) => item.status === 'pending') || [];
	const itemsToReviewCount = itemsToReview.length;
	const itemsToReviewTotal = itemsToReview.reduce(
		(sum, item) => sum + item.total,
		0,
	);

	const retainerFeeTotal = retainerFee?.total || 0;

	const motionProps = useStackAnimation(0.3, !isLoading);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-screen md:h-[85vh]">
				<Loader />
			</div>
		);
	}

	const summaryItems = [
		{ label: 'Budget', amount: budget?.total || 0 },
		{
			label: 'Total Paid',
			amount:
				invoices?.reduce(
					(sum, invoice) =>
						invoice.status === 'paid' ? sum + invoice.total : sum,
					0,
				) || 0,
		},
		{ label: 'Total Due', amount: openInvoicesTotal },
	];

	return (
		<div className="md-lg:px-5 lg:px-28">
			<SummaryCard items={summaryItems} isDataReady={!isLoading} />

			<Card
				border="lg"
				className="h-fit flex flex-col rounded-xl p-4 bg-background gap-2 w-fit md:rounded-3xl lg:rounded-3xl md:p-5 lg:p-5"
			>
				<ActionTitle title="Action Items" />
				{[
					{
						icon: 'document-duplicate',
						title: 'Open Proposals',
						count: openProposals.length,
						amount: openProposalsTotal,
						action: 'Approve Now',
					},
					{
						icon: 'inbox-arrow-down',
						title: 'Open Invoices',
						count: openInvoices.filter((invoice) => invoice.status === 'opened')
							.length,
						amount: openInvoicesTotal,
						action: 'Pay Now',
					},
					{
						icon: 'clipboard-document-check',
						title: 'Items to Review',
						count: itemsToReviewCount,
						amount: itemsToReviewTotal,
						action: 'Approve Now',
					},
					{
						icon: 'wallet',
						title: 'Retainer Fee',
						amount: retainerFeeTotal,
						action: 'Pay Now',
					},
				].map((item, index) => (
					<motion.div
						key={index}
						initial={motionProps.initial}
						animate={motionProps.animate}
						custom={index}
						className="flex flex-col shadow-lg gap-6 p-3 bg-gray border border-gray-200 rounded-lg md:flex-row lg:flex-row justify-between md:p-4 lg:p-4"
					>
						<div className="flex flex-row justify-between md:items-center space-x-3">
							<div className="flex gap-2">
								<Icon
									size="sm"
									name={item.icon}
									className="fill-primary-badge"
								/>
								<span className="font-medium whitespace-nowrap">
									{item.title}
								</span>
							</div>
							{item.count !== undefined && item.count > 0 && (
								<Badge
									size="sm"
									className="bg-primary-badge text-xs font-semibold"
								>
									{item.count}
								</Badge>
							)}
						</div>
						<div className="flex flex-row justify-between items-end md:items-center lg:items-center space-x-4">
							<span>
								<p className="flex items-baseline gap-1 mb-2 lg:mb-0">
									<FormatCurrency
										value={item.amount}
										className="font-dm-sans text-4xl md:text-2xl"
									/>
									<span className="text-xs text-gray-500 font-medium">
										Total
									</span>
								</p>
							</span>
							<Button size="sm" variant="primary" className="mt-2 md:mt-0 w-32">
								{item.action}
							</Button>
						</div>
					</motion.div>
				))}
			</Card>
		</div>
	);
};
